import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";

import {
  Button,
  CancelSubmitButtons,
  CardInputsContainer,
} from "~/common/components";
import { getDisabledClasses } from "~/common/components/ui/FieldsetCard";
import Input from "~/common/components/ui/Input";
import {
  hasConsultationAccess,
  hasGeneralAccess,
} from "~/common/utils/checkUserPermissions";
import {
  useCurrentTeamMemberContext,
  useUpdateTeamMemberDetails,
} from "~/providers/hooks";
import { teamMemberProfileSchemaWithPreferredReferenceValidation } from "~/providers/schemas/teamMembers";
import { TeamMemberProfileFormValuesType } from "~/providers/types/teamMembers";
import {
  getTeamMemberProfileDefaultValues,
  updateTeamMemberProfileFormToRequest,
} from "~/providers/utils";
import { ProfilePictureHandler } from "../profilePicture";

export const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();

  const { mutate: updateTeamMemberDetails } = useUpdateTeamMemberDetails();

  const currentTeamMember = useCurrentTeamMemberContext();

  const {
    handleSubmit,
    reset,
    watch,
    register,
    formState: { errors },
  } = useForm<TeamMemberProfileFormValuesType>({
    defaultValues: getTeamMemberProfileDefaultValues(currentTeamMember),
    resolver: zodResolver(
      teamMemberProfileSchemaWithPreferredReferenceValidation(
        currentTeamMember?.organization_team_members[0].organization.type,
      ),
    ),
  });

  const isConsultationOnly =
    hasConsultationAccess(currentTeamMember.user_permissions) &&
    !hasGeneralAccess(currentTeamMember.user_permissions);

  const onEdit = () => setIsEditing(true);

  const onCancel = () => {
    reset();
    setIsEditing(false);
  };

  const onSubmit: SubmitHandler<TeamMemberProfileFormValuesType> = (values) => {
    updateTeamMemberDetails(updateTeamMemberProfileFormToRequest(values), {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: ["getCurrentTeamMember"],
        });
        toast.success("Profile updated successfully");
        setIsEditing(false);
        reset(values);
      },
    });
  };

  return (
    <CardInputsContainer
      title="Profile"
      containerClassName="flex-row items-center"
      actions={
        isEditing ? (
          <CancelSubmitButtons
            containerClassName="w-auto"
            submitButtonLabel="Save"
            handleCancel={onCancel}
            handleSubmit={handleSubmit(onSubmit)}
            submitButtonType="button"
          />
        ) : (
          <Button onClick={onEdit} className="bg-white" variant="outline">
            Edit
          </Button>
        )
      }
    >
      <div className="flex size-full flex-col gap-4">
        <div className="flex items-center gap-3">
          <ProfilePictureHandler src={currentTeamMember.profile_picture_path} />
          <div className="flex flex-col">
            <p className="font-medium">
              {currentTeamMember.name} {currentTeamMember.last_name}
            </p>
            <p className="text-sm leading-5">
              {
                currentTeamMember.organization_team_members?.[0]?.organization
                  .name
              }
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <Input
              label="First name"
              disabled={!isEditing}
              error={errors.name?.message}
              {...getDisabledClasses(!isEditing)}
              {...register("name")}
            />
            <Input
              label="Last name"
              disabled={!isEditing}
              error={errors.last_name?.message}
              {...getDisabledClasses(!isEditing)}
              {...register("last_name")}
            />
          </div>
          <Input
            label="How would you like your name to appear in the signature of system-generated messages from you to patients?"
            disabled={!isEditing}
            placeholder="e.g., Dr. John Smith or John Smith, MD"
            error={errors.signature_name?.message}
            maxLength={200}
            charactersCount={watch("signature_name").length}
            {...getDisabledClasses(!isEditing)}
            {...register("signature_name")}
          />
          <Input
            containerClassName={twMerge(isConsultationOnly && "hidden")}
            disabled={!isEditing}
            label="How would you like to be referred to in system-generated messages to patients?"
            description={
              isEditing
                ? "For example, “Please contact Dr. Smith to schedule a 20 minute appointment”"
                : ""
            }
            placeholder="e.g., Dr. Smith"
            error={errors.preferred_reference?.message}
            maxLength={200}
            charactersCount={watch("preferred_reference")?.length}
            {...getDisabledClasses(!isEditing)}
            {...register("preferred_reference")}
          />
        </div>
      </div>
    </CardInputsContainer>
  );
};
